import React, { useEffect, useState } from "react";
import {
  fetchJournalEntries,
  createJournalEntry,
  deleteJournalEntry,
} from "../../api/ie.api";

const fmtINR = (n) =>
  Number(n || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
  }) : "—";

const ADJUSTMENT_TYPES = [
  { value: "accrual",      label: "Accrual" },
  { value: "provision",    label: "Provision" },
  { value: "depreciation", label: "Depreciation" },
  { value: "prepaid",      label: "Prepaid" },
  { value: "other",        label: "Other" },
];

const EMPTY_LINE = { ledgerName: "", type: "Dr", amount: "" };

export default function JournalEntriesTab({ societyId, fyId, financialYear }) {
  const [journals, setJournals]         = useState([]);
  const [loading, setLoading]           = useState(true);
  const [saving, setSaving]             = useState(false);
  const [error, setError]               = useState("");
  const [success, setSuccess]           = useState("");
  const [showForm, setShowForm]         = useState(false);

  // Form state
  const [date, setDate]                 = useState("");
  const [narration, setNarration]       = useState("");
  const [adjustmentType, setAdjType]    = useState("accrual");
  const [lines, setLines]               = useState([
    { ...EMPTY_LINE, type: "Dr" },
    { ...EMPTY_LINE, type: "Cr" },
  ]);

  useEffect(() => {
    loadJournals();
  }, [societyId, financialYear]);

  const loadJournals = async () => {
    try {
      setLoading(true);
      const res = await fetchJournalEntries(societyId, financialYear);
      setJournals(res.data || []);
    } catch (err) {
      setError("Failed to load journal entries.");
    } finally {
      setLoading(false);
    }
  };

  // ── Line management ─────────────────────────────────────────────────────
  const updateLine = (idx, field, value) => {
    setLines((prev) =>
      prev.map((l, i) => (i === idx ? { ...l, [field]: value } : l))
    );
  };

  const addLine = () => setLines((prev) => [...prev, { ...EMPTY_LINE }]);

  const removeLine = (idx) => {
    if (lines.length <= 2) return; // minimum 2 lines
    setLines((prev) => prev.filter((_, i) => i !== idx));
  };

  // ── Balance check ────────────────────────────────────────────────────────
  const totalDr = lines
    .filter((l) => l.type === "Dr")
    .reduce((s, l) => s + (Number(l.amount) || 0), 0);
  const totalCr = lines
    .filter((l) => l.type === "Cr")
    .reduce((s, l) => s + (Number(l.amount) || 0), 0);
  const isBalanced = Math.abs(totalDr - totalCr) < 0.01 && totalDr > 0;

  // ── Submit ───────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    setError("");
    setSuccess("");

    if (!date)      return setError("Please select a date.");
    if (!narration) return setError("Please enter a narration.");
    if (!isBalanced) {
      return setError(
        `Entry does not balance — Dr: ₹${fmtINR(totalDr)} | Cr: ₹${fmtINR(totalCr)}`
      );
    }

    const validLines = lines.filter(
      (l) => l.ledgerName.trim() && Number(l.amount) > 0
    );
    if (validLines.length < 2) {
      return setError("At least 2 valid lines (with ledger name and amount) are required.");
    }

    try {
      setSaving(true);
      await createJournalEntry({
        societyId,
        financialYear,
        date,
        narration,
        adjustmentType,
        entries: validLines.map((l) => ({
          ledgerName: l.ledgerName.trim(),
          type:       l.type,
          amount:     Number(l.amount),
        })),
      });
      setSuccess("Journal entry saved successfully.");
      resetForm();
      loadJournals();
    } catch (err) {
      const msg = err?.response?.data?.message || "Failed to save journal entry.";
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setDate("");
    setNarration("");
    setAdjType("accrual");
    setLines([
      { ...EMPTY_LINE, type: "Dr" },
      { ...EMPTY_LINE, type: "Cr" },
    ]);
    setShowForm(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this journal entry?")) return;
    try {
      await deleteJournalEntry(id);
      setJournals((prev) => prev.filter((j) => j._id !== id));
      setSuccess("Journal entry deleted.");
    } catch {
      setError("Failed to delete entry.");
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div>

      {/* Messages */}
      {error   && <div className="ie-alert error"   style={{ marginBottom: 16 }}><span>⚠</span><span>{error}</span></div>}
      {success && <div className="ie-alert success" style={{ marginBottom: 16 }}><span>✓</span><span>{success}</span></div>}

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 600, color: "#2a221c" }}>
            Journal Entries
          </div>
          <div style={{ fontSize: 12, color: "#9a8f85", marginTop: 2 }}>
            Year-end adjustments — accruals, provisions, depreciation
          </div>
        </div>
        <button
          className="ie-btn purple"
          onClick={() => { setShowForm(true); setError(""); setSuccess(""); }}
        >
          + New Entry
        </button>
      </div>

      {/* ── Entry Form ────────────────────────────────────────────────────── */}
      {showForm && (
        <div className="ie-card" style={{ marginBottom: 24 }}>
          <div className="ie-card-header">
            <div className="ie-card-title">New Journal Entry</div>
            <button className="ie-btn" onClick={resetForm} style={{ padding: "5px 10px", fontSize: 12 }}>
              ✕ Cancel
            </button>
          </div>
          <div className="ie-card-body">
            <div className="je-form">

              {/* Row 1: Date, Type, Narration */}
              <div className="je-form-row triple">
                <div className="je-field">
                  <label>Date</label>
                  <input
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                  />
                </div>
                <div className="je-field">
                  <label>Adjustment Type</label>
                  <select
                    value={adjustmentType}
                    onChange={(e) => setAdjType(e.target.value)}
                  >
                    {ADJUSTMENT_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>
                <div className="je-field">
                  <label>Narration</label>
                  <input
                    type="text"
                    placeholder="e.g. Outstanding electricity charges payable"
                    value={narration}
                    onChange={(e) => setNarration(e.target.value)}
                  />
                </div>
              </div>

              {/* Entry lines */}
              <div>
                <div className="je-lines-head">
                  <span>Ledger / Head Name</span>
                  <span>Dr / Cr</span>
                  <span style={{ textAlign: "right" }}>Amount (₹)</span>
                  <span></span>
                </div>

                {lines.map((line, idx) => (
                  <div className="je-line-row" key={idx}>
                    <input
                      type="text"
                      placeholder="e.g. Electricity Charges"
                      value={line.ledgerName}
                      onChange={(e) => updateLine(idx, "ledgerName", e.target.value)}
                    />
                    <select
                      value={line.type}
                      className={line.type === "Dr" ? "dr" : "cr"}
                      onChange={(e) => updateLine(idx, "type", e.target.value)}
                    >
                      <option value="Dr">Dr</option>
                      <option value="Cr">Cr</option>
                    </select>
                    <input
                      type="number"
                      placeholder="0.00"
                      value={line.amount}
                      min="0"
                      style={{ textAlign: "right" }}
                      onChange={(e) => updateLine(idx, "amount", e.target.value)}
                    />
                    <button
                      className="ie-btn danger"
                      style={{ padding: "4px 8px", fontSize: 14 }}
                      onClick={() => removeLine(idx)}
                      disabled={lines.length <= 2}
                    >
                      ✕
                    </button>
                  </div>
                ))}

                {/* Balance indicator */}
                <div className="je-balance-row">
                  <div className="je-balance-item">
                    <span className="je-balance-label">Total Dr</span>
                    <span className="je-balance-val dr">₹{fmtINR(totalDr)}</span>
                  </div>
                  <div className="je-balance-item">
                    <span className="je-balance-label">Total Cr</span>
                    <span className="je-balance-val cr">₹{fmtINR(totalCr)}</span>
                  </div>
                  <div className="je-balance-item">
                    <span className="je-balance-label">Difference</span>
                    <span className={`je-balance-val ${isBalanced ? "cr" : "unbalanced"}`}>
                      {isBalanced ? "✓ Balanced" : `₹${fmtINR(Math.abs(totalDr - totalCr))}`}
                    </span>
                  </div>
                </div>

                <button
                  className="ie-btn"
                  onClick={addLine}
                  style={{ marginTop: 4, fontSize: 12 }}
                >
                  + Add Line
                </button>
              </div>

              {/* Submit */}
              <div style={{ display: "flex", gap: 10, paddingTop: 8 }}>
                <button
                  className="ie-btn primary"
                  onClick={handleSubmit}
                  disabled={saving || !isBalanced}
                >
                  {saving ? "Saving..." : "Save Journal Entry"}
                </button>
                <button className="ie-btn" onClick={resetForm}>
                  Cancel
                </button>
              </div>

            </div>
          </div>
        </div>
      )}

      {/* ── Entries list ──────────────────────────────────────────────────── */}
      <div className="ie-card">
        <div className="ie-card-header">
          <div className="ie-card-title">
            All Journal Entries
            <span style={{
              marginLeft: 8, fontSize: 12, fontWeight: 400,
              color: "#9a8f85",
            }}>
              ({journals.length})
            </span>
          </div>
        </div>
        <div className="ie-card-body" style={{ padding: 0 }}>
          {loading ? (
            <div className="ie-loading">
              <div className="ie-spinner"></div>Loading...
            </div>
          ) : journals.length === 0 ? (
            <div className="ie-empty">
              <div className="ie-empty-icon">📋</div>
              <div className="ie-empty-text">No journal entries yet</div>
              <div className="ie-empty-sub">
                Add year-end adjustments like accruals, provisions and depreciation
              </div>
            </div>
          ) : (
            <table className="je-table" style={{ margin: 0 }}>
              <thead>
                <tr>
                  <th style={{ width: 100 }}>Date</th>
                  <th>Narration</th>
                  <th style={{ width: 110 }}>Type</th>
                  <th style={{ width: 80 }}>Lines</th>
                  <th style={{ width: 120, textAlign: "right" }}>Dr Total</th>
                  <th style={{ width: 80 }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {journals.map((j) => {
                  const drTotal = j.entries
                    .filter((e) => e.type === "Dr")
                    .reduce((s, e) => s + e.amount, 0);
                  return (
                    <tr key={j._id}>
                      <td style={{ fontSize: 12 }}>{fmtDate(j.date)}</td>
                      <td>
                        <div style={{ fontWeight: 500, fontSize: 13 }}>{j.narration}</div>
                        <div style={{ fontSize: 11, color: "#9a8f85", marginTop: 3 }}>
                          {j.entries.map((e, i) => (
                            <span key={i} style={{ marginRight: 8 }}>
                              <span style={{ color: e.type === "Dr" ? "#1a5276" : "#276749", fontWeight: 600 }}>
                                {e.type}
                              </span>{" "}
                              {e.ledgerName} — ₹{fmtINR(e.amount)}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td>
                        <span className={`je-type-badge ${j.adjustmentType}`}>
                          {j.adjustmentType}
                        </span>
                      </td>
                      <td style={{ textAlign: "center", color: "#9a8f85" }}>
                        {j.entries.length}
                      </td>
                      <td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums", fontWeight: 500 }}>
                        ₹{fmtINR(drTotal)}
                      </td>
                      <td>
                        <button
                          className="ie-btn danger"
                          style={{ padding: "4px 10px", fontSize: 12 }}
                          onClick={() => handleDelete(j._id)}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

    </div>
  );
}
